/**
 * The screen the business owner opens, and the one the product is judged on.
 *
 * THE STEER: this is for owners and stakeholders. Data should be interesting to THEM,
 * not technical numbers they do not understand. There is no latency here, no token
 * count, no end reason and no cost. That used to be because those numbers lived on an
 * internal screen; the screen is gone and they are simply not shown anywhere, which is
 * what lets this page be written entirely in the owner's language.
 *
 * Four habits carry the whole page.
 *
 * EVERY NUMBER IS A SENTENCE. "Handled end to end" rather than "resolved"; "including 14
 * when nobody was there" under the call count. A tile whose label needs explaining is a
 * tile that gets skipped.
 *
 * EVERY CHART CARRIES A CAPTION. One plain sentence saying what it means. A chart
 * without one is a chart an owner does not read, and the caption is where the actual
 * insight lives - the picture is the evidence for it. When the picture is suppressed the
 * caption is drawn in a bordered box instead of as loose prose, so a thin window reads as
 * a page that answered rather than as a page that failed to load - see `Figure`.
 *
 * EVERY NUMBER WORTH ACTING ON IS CLICKABLE. "Needs your attention: 4" is useless as a
 * number and useful as four rows with names on them, so every tile links into the calls
 * list with the filter already applied. The rows themselves are NOT reprinted here: a
 * "Waiting on you" table used to sit under the tiles, and it was the calls log with three
 * of its columns missing, one click from the tile that already said how many there were.
 *
 * A CHART WITH NO SHAPE IS NOT DRAWN. Taken from the caption rule: if the sentence is
 * where the insight lives, a bar at 100% and a heatmap with one lit square are evidence
 * of nothing, and a quiet week that renders eight of them buries the panels that do mean
 * something. Below the floor the caption becomes the whole panel - see `hasShape`.
 *
 * THE CHARTS ON THIS PAGE WEAR THE PRODUCT'S ACCENT, which `styles.css` used to forbid on
 * the grounds that a bar the colour of a link makes an owner try to click the chart. On
 * this page that is not a failure mode, it is the fourth habit: every cell of the stat
 * strip is a link, and the bars in "what callers wanted" and the rows of the mood ring are
 * drill-throughs into the calls list. The one thing on the screen an owner might press and
 * the one thing they must not still do not share a colour, because the pressable one -
 * the range picker's selected segment, a primary button - is ink.
 *
 * WHY THERE IS ONE SCREEN AND NOT TWO. There used to be an /insights beside this, split
 * on cadence: "what happened" daily here, "is it getting better" monthly there. The
 * split did not survive contact - six of its seven panels were single-window breakdowns
 * shaped exactly like these, and its funnel was built from the same `overviewCounts` row
 * as the tiles above, so `answered`, `success` and `handed off` WERE three of these tiles
 * redrawn as bars. What an owner got was one page of numbers and a second restating them
 * in different words.
 *
 * The rest of that page came here as a "More detail" disclosure, and it has now gone the
 * same way for the same reason. Six more panels behind a fold is a second dashboard that
 * happens to be closed: the ones that read at all - grades, moods, durations, locations,
 * repeat callers - were slices of the same window already drawn above, and on any
 * realistic week most of them had too little in them to draw and printed a sentence
 * apologising for it. The endpoint they were fetched from, `/v1/insights`, now has no
 * caller in the dashboard.
 */

import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi, type OverviewPage } from "../api";
import { PAGE_WIDTH, PageHead } from "../components/Page";
import {
  Columns,
  RangePicker,
  Stack,
  Stats,
  State,
  useWindow,
  type StatCell,
} from "../components/Internal";
import { useScopedTo } from "../components/Shell";
import { useAgent } from "../currentAgent";
import {
  BAND_LABELS,
  BANDS,
  BarList,
  Figure,
  FigureNote,
  Heatmap,
  Legend,
  MOODS,
  MOOD_LABELS,
  SentimentPie,
  StackedDays,
  WeeklyColumns,
  fillDays,
  hasShape,
  isMood,
} from "../charts";
import { against, count, duration, labelFor, money, share } from "../format";

/** A heatmap of 168 cells needs more than a handful of calls before a pattern exists. */
const HEATMAP_FLOOR = 8;

export function Overview() {
  const [params] = useSearchParams();
  const { since, until, days } = useWindow();
  const test = params.get("test") === "true";
  const agent = useAgent();

  const query = `days=${days}&until=${until}&test=${test}&agent=${encodeURIComponent(agent.id)}`;
  const { data, error, loading } = useApi<OverviewPage>(`/v1/overview?${query}`);

  return (
    <>
      <PageHead
        contain={PAGE_WIDTH}
        // The BUSINESS's name, which is what an owner with two of them needs at the top of
        // the page - known from the address before the numbers arrive.
        title={agent.name}
        lede={`What your agent answered over the last ${days} days, and what came of it.`}
        actions={<RangePicker />}
      />

      <div className={PAGE_WIDTH}>
        <State loading={loading} error={error} empty={!!data && data.headline.calls === 0} />
        {data && data.headline.calls > 0 && (
          <Body data={data} days={days} since={since} until={until} query={query} />
        )}
      </div>
    </>
  );
}

/**
 * Everything below the head, once there is something to draw.
 *
 * EXPORTED FOR THE TEST and for nothing else, the same way `Row` is in `Calls.tsx`. The
 * alternative is a suite that stubs `fetch` to reach the one thing worth asserting here,
 * which tests the fetch hook rather than the page.
 */
export function Body({
  data,
  days,
  since,
  until,
  query,
}: {
  data: OverviewPage;
  days: number;
  since: number;
  until: number;
  query: string;
}) {
  const navigate = useNavigate();
  const scoped = useScopedTo();
  const head = data.headline;
  const words = data.vocabulary;
  const hours = data.afterHours;
  // The business's own clock. Every time on this page is printed in it, so a call at 7pm
  // in Sydney reads as 7pm here and sits in the 7pm column of the heatmap - which it would
  // not if one of the two used the reader's browser instead.
  const tz = data.agent.timezone;

  // Every tile that means "go and look at these calls" links into the list with the
  // filter already applied, because the filters live in the URL. `scoped` puts it under the
  // agent and carries the test-call setting.
  const to = (extra: string) => scoped(`/calls?${extra}`);

  // The Worker's own squares, so the outline and the percentage beside it cannot disagree.
  const openFor = (dow: number, hour: number) => Boolean(hours.openSquares?.[dow]?.[hour]);

  /*
    THE CELLS ARE DATA, NOT MARKUP, because the strip has to know how many there are and
    how wide each wants to be before it can draw a single row of them. `data.value` returns
    one cell per money basis and may return none, so this list is four long on a business
    that has never quoted and seven on one that quotes, books and estimates.

    NO ICONS. They were there to give a label a second, faster reading; at five cells in one
    fused card they were five small marks competing with five large numbers, which is the
    opposite of what the strip is for.
  */
  const cells: StatCell[] = [
    {
      key: "calls",
      label: "Calls answered",
      value: count(head.calls),
      note: hours.closed
        ? `${hours.closed} came in when nobody was there`
        : against(head.calls, head.callsBefore, days),
    },
    {
      // The number that justifies the invoice, and the reason it is named in full rather
      // than as "resolved": the agent finished the job and nobody else was needed.
      key: "handled",
      label: "Handled end to end",
      value: count(head.handledEndToEnd),
      note: head.calls
        ? `${Math.round((head.handledEndToEnd / head.calls) * 100)}% of calls`
        : undefined,
      href: to("resolved=true"),
    },
    {
      // The business's own noun. A dentist's cell says "Bookings" off the same build.
      key: "success",
      label: capitalise(words.nounPlural),
      value: count(head.success),
      note: against(head.success, head.successBefore, days),
      href: to(`group=${words.successGroups[0] ?? ""}`),
      weight: 0.9,
      zero: head.success === 0,
    },
    ...data.value.map((row) => ({
      key: row.basis ?? "value",
      // NEVER "revenue". This is what an agent read out over the phone from a price sheet,
      // and the cell is labelled with its basis so it cannot overclaim. These cells are the
      // ONLY place money appears on this page.
      label: `Value ${basisWord(row.basis)}`,
      value: money(row.minor, row.currency),
      note: `across ${row.calls} call${row.calls === 1 ? "" : "s"}`,
      // A step down in size and a step up in width: `A$11,694` is a string where every
      // other cell holds a two-digit number.
      wide: true,
      weight: 1.4,
    })),
    {
      // PEOPLE, NOT CALLS: somebody who had never rung before. Three calls from Mark are
      // three calls above and one contact here.
      key: "new-contacts",
      label: "New contacts",
      value: count(head.newContacts),
      note: against(head.newContacts, head.newContactsBefore, days),
      href: scoped("/contacts"),
      weight: 0.9,
      zero: head.newContacts === 0,
    },
    {
      key: "attention",
      label: "Needs your attention",
      value: count(head.attention),
      // The note IS the invitation, and the whole cell is the link that honours it. A
      // second link inside a link would not be valid markup and would not be a second
      // destination anyway.
      // OPEN, NOT MERELY UNRESOLVED. The Worker counts a call here while nobody has marked
      // it handled, so a call somebody dealt with at the counter leaves this number the
      // moment they say so - and the link opens exactly those calls, rather than every call
      // the agent ever failed to resolve, which is a list that only grows.
      note: head.attention
        ? "Urgent, unresolved or escalated, and nobody has handled it yet →"
        : "nothing outstanding",
      href: to("open=true"),
      tone: head.attention ? ("alert" as const) : undefined,
      weight: 1.2,
      zero: head.attention === 0,
    },
  ];

  return (
    <>
      <Stats cells={cells} />

      <Columns>
        <Stack>
          <Figure
            title="When people call you"
            /* THE BUSINESS'S CLOCK, NAMED. Every hour on this chart is the agent's local
               hour, so a reader in London looking at a Sydney business has to be told
               whose 7pm the dark square is. */
            aside={tz && <span className="text-xs text-ink-faint">Times in {tz}</span>}
            enough={head.calls >= HEATMAP_FLOOR && hours.share !== null}
            caption={
              hours.share === null
                ? "No call has a known local time yet, so this is empty rather than wrong."
                : `${share(hours.share)} of your calls came in outside ${
                    hours.configured ? "your opening hours" : "the default 9-to-5"
                  } — ${hours.closed} of ${hours.open + hours.closed}. ${
                    head.calls >= HEATMAP_FLOOR
                      ? `Outlined squares are ${hours.description}.`
                      : `Too few calls to show when they cluster; your hours are ${hours.description}.`
                  }`
            }
          >
            <Heatmap grid={data.when.grid} isOpen={openFor} />
            {!hours.configured && (
              // The share is measured against OUR default hours, and a page that did not
              // say so would be reporting a confident percentage about 9-to-5 to a
              // business that opens at seven.
              <FigureNote>
                These are our default hours.{" "}
                <Link className="text-accent hover:underline" to={scoped("/settings?tab=hours")}>
                  Set your real ones
                </Link>{" "}
                and every figure on this page follows.
              </FigureNote>
            )}
            {data.when.unknown > 0 && (
              <FigureNote>
                {data.when.unknown} call{data.when.unknown === 1 ? " has" : "s have"} no local time
                and {data.when.unknown === 1 ? "is" : "are"} not on this chart.
              </FigureNote>
            )}
          </Figure>

          <Mood data={data} to={to} />
        </Stack>

        <Stack>
          <Trend data={data} days={days} since={since} until={until} />

          <Figure
            title="What callers wanted"
            aside={<Aside to={scoped("/settings?tab=tracking")}>Edit reasons</Aside>}
            enough={hasShape(head.calls, data.intents.length)}
            caption={
              !data.intents.length
                ? "Nothing has been read yet, so there is nothing to group."
                : hasShape(head.calls, data.intents.length)
                  ? `The reasons people rang, from ${data.agent.name}'s own list of categories. A call can count under more than one. Anything that did not fit is counted under “other” — click that bar to read those calls, and add the category they were missing.`
                  : `Too few calls to group yet. ${listOf(
                      data.intents.map((row) => labelFor(words.labels, row.key)),
                    )} so far.`
            }
          >
            <BarList
              total={head.calls}
              max={8}
              // The same move the outcome bars make below, and here it is worth more: the
              // biggest bar on a business whose list is too short is “other”, and the only
              // way to write the category that was missing is to read the calls in it.
              onSelect={(key) => navigate(to(`intent=${encodeURIComponent(key)}`))}
              data={data.intents.map((row) => ({
                key: row.key ?? "other",
                label: labelFor(words.labels, row.key),
                value: row.calls,
              }))}
            />
          </Figure>

          <Weakness data={data} />
        </Stack>
      </Columns>


      <p className="mt-5 text-xs text-ink-faint">
        {count(head.calls)} call{head.calls === 1 ? "" : "s"}
        {/* Omitted rather than printed as "0s" when no call carried a duration - which
            is the normal state for a backlog replayed from records written before there
            was anywhere to put one. A zero here reads as a bug in the phone system. */}
        {head.minutes > 0 && ` · ${duration(head.minutes * 60000)} on the phone`}
        {` · ${head.handoffs} handed to a person`}
      </p>
    </>
  );
}

/**
 * Which of the five measures is most often the weak one.
 *
 * TWO FACTS PER BAR AND THEY MUST NOT SUM. The bar's LENGTH is how many calls came back
 * none-or-low on that measure - they told us and the answer was thin, which is about the
 * caller. The note under it is how many came back `unknown` - nobody established it, which
 * is about the AGENT and is the one an agent author can actually fix. Adding the second
 * into the first would produce one number that answers neither question, which is why the
 * Worker returns the levels raw rather than pre-bucketed into "weak".
 */
/**
 * How callers sounded, as a ring.
 *
 * THE DENOMINATOR IS CALLS THAT WERE READ, NOT CALLS ANSWERED, and getting that wrong is
 * the one way this panel could lie. `sentiments()` in the Worker filters
 * `analysis_sentiment IS NOT NULL`, so a window with an analyser backlog - or with no
 * `OPENAI_API_KEY` at all, where every call lands `skipped` - has fewer readings than
 * calls. Drawing three shares against the tile above would put a ring on the screen whose
 * slices visibly do not sum to the number an owner just read. So the total is summed from
 * the rows, and the gap is stated underneath rather than hidden.
 *
 * THE CAPTION OWES ONE CAVEAT THE OTHER PANELS DO NOT. `neutral` is also where a reading
 * the model fumbled lands - `z.enum(SENTIMENTS).catch("neutral")` in the Worker's schema -
 * so a neutral slice is "sounded neutral" plus "we could not tell", and `CallReading.tsx`
 * already suppresses neutral on a call where nobody spoke for exactly that reason. An
 * owner comparing this panel against calls they remember deserves to know that.
 */
function Mood({ data, to }: { data: OverviewPage; to: (filter: string) => string }) {
  const navigate = useNavigate();

  // `isMood` rather than a cast: the column is three values now, but a row read before
  // migration 0010 - or by a Worker mid-deploy - could still carry the old fourth, and a
  // slice with no colour is worse than a call left out of the ring.
  const slices = MOODS.map((mood) => ({
    mood,
    calls: data.sentiments.find((row) => row.key === mood)?.calls ?? 0,
  }));
  const read = data.sentiments
    .filter((row) => isMood(row.key))
    .reduce((sum, row) => sum + row.calls, 0);
  const lit = slices.filter((slice) => slice.calls).length;
  const unread = data.headline.calls - read;
  const top = [...slices].sort((a, b) => b.calls - a.calls)[0]!;

  return (
    <Figure
      title="How callers sounded"
      enough={hasShape(read, lit)}
      caption={
        !read
          ? "No call in this window has been read yet, so there is nothing to group."
          : hasShape(read, lit)
            ? `How the caller came across, on the ${read} call${
                read === 1 ? "" : "s"
              } that have been read. Neutral is also where a call we could not read clearly lands, so it runs a little large. Click a row to read those calls.`
            : `Too few calls read to show a split. ${top.calls} of ${read} sounded ${MOOD_LABELS[
                top.mood
              ].toLowerCase()} so far.`
      }
    >
      <SentimentPie
        slices={slices}
        total={read}
        onSelect={(mood) => navigate(to(`sentiment=${mood}`))}
      />
      {unread > 0 && (
        <FigureNote>
          {unread} call{unread === 1 ? " has" : "s have"} not been read yet and{" "}
          {unread === 1 ? "is" : "are"} not in this split.
        </FigureNote>
      )}
    </Figure>
  );
}

function Weakness({ data }: { data: OverviewPage }) {
  const rows = MEASURE_ORDER.map((dimension) => {
    const levels = data.qualification.measures.filter((row) => row.dimension === dimension);
    const at = (...wanted: string[]) =>
      levels.filter((row) => wanted.includes(row.level)).reduce((sum, row) => sum + row.calls, 0);
    const example = levels.find((row) => wanted(row.level) && row.example)?.example ?? null;
    return { dimension, weak: at("none", "low"), unknown: at("unknown"), example };
  }).filter((row) => row.weak > 0 || row.unknown > 0);

  const ranked = [...rows].sort((a, b) => b.weak - a.weak || b.unknown - a.unknown);
  const top = ranked[0];

  return (
    <Figure
      title="Where enquiries fall down"
      enough={hasShape(data.headline.calls, ranked.length)}
      caption={
        !ranked.length
          ? "No call in this window has been read for a lead yet, so there is nothing to rank."
          : hasShape(data.headline.calls, ranked.length)
            ? `The part of an enquiry that most often comes back thin. ${
                top ? `${MEASURE_WORDS[top.dimension]} leads it on ${top.weak} call${
                  top.weak === 1 ? "" : "s"
                }.` : ""
              } “Never established” beside a bar is the agent's half of it — those are calls where nobody asked.`
            : "Too few calls read for a lead yet to say which part of an enquiry is weakest."
      }
    >
      <BarList
        unit="calls"
        /* THE ONE PANEL DRAWN AS STACKED ROWS. Its note is not a fragment, it is the second
           of the row's two figures written out in words, and nobody reads that truncated
           into the 200px name column the inline layout uses. */
        layout="stacked"
        data={ranked.map((row) => ({
          key: row.dimension,
          label: MEASURE_WORDS[row.dimension] ?? row.dimension,
          value: row.weak,
          note: row.unknown
            ? `Never established on ${row.unknown} call${row.unknown === 1 ? "" : "s"}.`
            : row.example,
          // GREY WHERE THE BAR IS NOT THE STORY. The two figures on a row must not be
          // added, and on a row where more calls went unasked than came back thin, the
          // bar's length under-reports the row. Grey says read the sentence.
          tone: row.unknown > row.weak ? ("muted" as const) : ("accent" as const),
        }))}
      />
    </Figure>
  );
}

/** Fixed order: the three that score, then the two that do not. */
const MEASURE_ORDER = ["intent", "urgency", "fit", "reach", "momentum"];

const MEASURE_WORDS: Record<string, string> = {
  intent: "What they wanted",
  urgency: "How soon",
  fit: "Whether you do it",
  reach: "A way to reach them",
  momentum: "Something agreed",
};

/** The levels that mean "they told us and the answer was thin". */
function wanted(level: string): boolean {
  return level === "none" || level === "low";
}

/**
 * Calls over time, at the resolution the window can actually support.
 *
 * ONE PANEL, NOT TWO. A day-by-day stack and a week-by-week column chart of the same
 * rows used to sit on two different screens; they are the same predicate over the same
 * `local_day IS NOT NULL` rows, so which one an owner wants is decided by the window and
 * not by which page they are on. Seven days of weekly columns is one column, and ninety
 * days of daily stacks is ninety slivers.
 */
function Trend({
  data,
  days,
  since,
  until,
}: {
  data: OverviewPage;
  days: number;
  since: number;
  until: number;
}) {
  const words = data.vocabulary;
  const daily = days <= 7;

  const stacks = fillDays(data.outcomesByDay, words.successGroups, { since, until });
  const bandsInUse = BANDS.filter((band) => stacks.some((day) => day.bands[band] > 0));

  // A trend needs at least two points to be a trend. One lit column is a number that has
  // been drawn as tall as the panel, which reads as a finding and is not one.
  const buckets = daily ? stacks.filter((day) => day.total > 0).length : data.weeks.length;
  const enough = buckets >= 2;

  const unit = daily ? "day" : "week";
  const calls = data.headline.calls;
  const success = data.headline.success;
  const thin = buckets === 0
    ? "No call in this window has a known local time, so there is nothing to trend."
    : `${
        buckets === 1 ? `Only one ${unit} in this window has` : `Only ${buckets} ${unit}s in this window have`
      } calls in ${buckets === 1 ? "it" : "them"}, so there is no trend to draw yet. ${count(
        calls,
      )} call${calls === 1 ? "" : "s"}, ${count(success)} ${
        success === 1 ? words.noun : words.nounPlural
      }.`;

  return (
    <Figure
      title={daily ? "What happened, day by day" : "Week by week"}
      enough={enough}
      aside={enough && daily ? <Legend bands={bandsInUse} /> : undefined}
      caption={
        !enough
          ? thin
          : daily
            ? `Every call in the last ${days} days, grouped into what it came to. ${
                BAND_LABELS.won
              } is whatever this business counts as a good outcome — ${words.successGroups.join(
                ", ",
              )}.`
            : `Calls taken each week, with the ${words.nounPlural} among them drawn inside.
               Both are counts of calls, so they share one scale — the shaded part is the
               proportion that went well, read directly rather than off a second axis.`
      }
    >
      {daily ? (
        <StackedDays days={stacks} bands={bandsInUse} />
      ) : (
        <WeeklyColumns weeks={data.weeks} successLabel={capitalise(words.nounPlural)} />
      )}
    </Figure>
  );
}

/**
 * The link a panel carries in its title row.
 *
 * Small enough to be a utility string, and a component because it appears twice and both
 * of them go to the same screen: a panel that tells you what the agent could not answer is
 * only useful beside the place you write the answer.
 */
function Aside({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link className="text-xs font-medium text-accent hover:underline" to={to}>
      {children}
    </Link>
  );
}

/** "Pricing", "Pricing and warranty", "Pricing, warranty and one other" - for a thin window. */
function listOf(items: string[]): string {
  const [first, second, ...rest] = items;
  if (!first) return "Nothing";
  if (!second) return first;
  if (!rest.length) return `${first} and ${second}`;
  return `${first}, ${second} and ${rest.length} other${rest.length === 1 ? "" : "s"}`;
}

/** "quoted", "booked" - the word that keeps a figure from being read as revenue. */
function basisWord(basis: string | null): string {
  if (!basis) return "recorded";
  return (
    { indicative_quote: "quoted", booked: "booked", estimate: "estimated" }[basis] ??
    basis.replace(/_/g, " ")
  );
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
